import { getContext, setContext } from 'svelte';
import type { Readable, Writable } from 'svelte/store';
import type { ReleaseRequest } from '../types/release';
import type { ReleaseRuntimeState } from '../stores/release';
import { releaseStore } from '../stores/release';

export const RELEASE_CONTEXT_KEY = Symbol('release-context');

/**
 * Phase 3: Cleaner context interface
 * 
 * Instead of passing many individual derived stores, we pass:
 * - releaseConfigState: The writable config store
 * - releaseRuntimeState: The writable runtime store
 * - releaseStore: Helper methods for state mutations
 * 
 * Child components can derive any values they need from these two core stores.
 */
export interface ReleaseContextValue {
	// Core state stores
	releaseConfigState: Writable<ReleaseRequest>;
	releaseRuntimeState: Readable<ReleaseRuntimeState> | Writable<ReleaseRuntimeState>;
	
	// State mutation helpers
	releaseStore: typeof releaseStore;
}

export function provideReleaseContext(value: ReleaseContextValue) {
	setContext(RELEASE_CONTEXT_KEY, value);
}

export function useReleaseContext(): ReleaseContextValue {
	const context = getContext<ReleaseContextValue>(RELEASE_CONTEXT_KEY);
	if (!context) {
		throw new Error('Release context is not available. Ensure provideReleaseContext is called in a parent component.');
	}
	return context;
}
